/**
 * The audience count, as an inline display value — no border, no action. Colour is inherited from the
 * surrounding context (a muted meta row in the list, foreground in the detail's segment), so the same
 * component reads correctly everywhere:
 *
 * - `counted` → "1,544 (7.7% of 20,000)" (with the audience icon, or plain text when `icon` is null).
 * - `counted` with `members: 0` → "0 (0.0% of 20,000)": a SUCCESS, rendered like any other count.
 * - `not-run` → "Unknown".
 * - `error`   → "Unknown" + a danger exclamation whose OWN hover/focus tooltip carries the warehouse's
 *               message (the icon is the trigger — same shape as the {@link StaleIndicator} warning icon).
 *
 * The detail page leads its definition pane with {@link AudienceSizeBar} instead, which states the count
 * as a headline and draws its share as a bar; it falls back to this badge for the two states that have no
 * number (`not-run`, `error`). The list and chat card use it bare — they can't run a count.
 */
import { CircleAlert, type LucideIcon, Users } from 'lucide-react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { MetaChip } from '@/_shared/components/list/MetaChip';
import { formatGrouped } from '@/_shared/lib/format';
import { cn } from '@/_shared/lib/utils';

import type { CountState } from '../types';

// The count leads, its denominator in parens. One decimal place always, per product-scope.md's format —
// `percentage` is already rounded to 1 decimal upstream, but `.toFixed(1)` re-pads a whole number like
// `50` to `50.0`.
//
// `formatGrouped`, not `toLocaleString()`: the size bar pins `en` and this badge did not, so the same
// audience read "20.000" in the list and "20,000" on the detail page of a de-DE browser.
function formatCount(members: number, universe: number, percentage: number): string {
  return `${formatGrouped(members)} (${percentage.toFixed(1)}% of ${formatGrouped(universe)})`;
}

export interface CountBadgeProps {
  state: CountState;
  /**
   * The leading icon. Defaults to the audience `Users` glyph so it reads as a peer to the warehouse chip
   * in the list. Pass `null` for plain text with no icon (the chat artifact card, whose tile already flags
   * the audience).
   */
  icon?: LucideIcon | null;
  className?: string;
}

export function CountBadge({ state, icon = Users, className }: CountBadgeProps): React.ReactElement {
  const value = (label: string): React.ReactElement =>
    icon ? (
      <MetaChip icon={icon} className={className}>
        {label}
      </MetaChip>
    ) : (
      <span className={cn('truncate', className)}>{label}</span>
    );

  if (state.status === 'counted') return value(formatCount(state.members, state.universe, state.percentage));
  if (state.status === 'not-run') return value('Unknown');

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      {icon ? <MetaChip icon={icon}>Unknown</MetaChip> : <span className="truncate">Unknown</span>}
      <ActionTooltip show message={state.error} className="relative z-10 items-center text-danger">
        <CircleAlert className="size-3.5" aria-hidden />
        <span className="sr-only">Failed to calculate size</span>
      </ActionTooltip>
    </span>
  );
}
